import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Printer, BookOpen } from "lucide-react";

const HANDBOOK_VERSION = "v1.0";
const HANDBOOK_LAST_UPDATED = "Feb 2026";

/**
 * Each section is a self-contained block of operator-manual prose.
 * `id` becomes the anchor; `title` renders in the TOC and the body heading.
 */
const SECTIONS = [
  { id: "getting-started", title: "1. Getting Started & Account Setup" },
  { id: "employees", title: "2. Employees" },
  { id: "daily-punches", title: "3. Daily Punches (Time Entries)" },
  { id: "jobs", title: "4. Jobs & Job Entries" },
  { id: "submissions", title: "5. Daily Submissions & Approvals" },
  { id: "commissions", title: "6. Commissions" },
  { id: "payroll", title: "7. Payroll & Paystubs" },
  { id: "phase1", title: "8. Phase 1 — Pre-Close Back-Fix (8-Day Window)" },
  { id: "phase2", title: "9. Phase 2 — Late-Submission Lane" },
  { id: "reimbursements", title: "10. Reimbursements" },
  { id: "time-off", title: "11. Time Off" },
  { id: "reports", title: "12. Reporting & Exports" },
  { id: "billing", title: "13. Billing & Subscription" },
  { id: "growth", title: "14. AI Growth Center" },
  { id: "marketing", title: "15. Public Marketing Surfaces" },
  { id: "playbooks", title: "16. Common VA Playbooks" },
  { id: "escalation", title: "17. Escalation & Support" },
];

/* ───────────────────────── small presentational helpers ───────────────────────── */

function H2({ id, children }) {
  return (
    <h2
      id={id}
      className="hb-h2 text-2xl md:text-3xl font-bold font-display text-slate-900 mt-14 mb-4 scroll-mt-24"
    >
      {children}
    </h2>
  );
}
function H3({ children }) {
  return <h3 className="hb-h3 text-lg md:text-xl font-semibold text-slate-900 mt-8 mb-2">{children}</h3>;
}
function P({ children }) {
  return <p className="hb-p text-[15px] md:text-base leading-[1.75] text-slate-700 mb-4">{children}</p>;
}
function OL({ children }) {
  return <ol className="hb-ol list-decimal pl-6 space-y-2 text-[15px] md:text-base leading-[1.75] text-slate-700 mb-4">{children}</ol>;
}
function UL({ children }) {
  return <ul className="hb-ul list-disc pl-6 space-y-1 text-[15px] md:text-base leading-[1.75] text-slate-700 mb-4">{children}</ul>;
}
function Note({ children }) {
  return (
    <div className="hb-callout hb-note my-4 border-l-4 border-sky-500 bg-sky-50 px-4 py-3 rounded-r text-[14px] md:text-[15px] leading-relaxed text-slate-700">
      <span className="font-semibold text-sky-800">Note. </span>{children}
    </div>
  );
}
function Warn({ children }) {
  return (
    <div className="hb-callout hb-warn my-4 border-l-4 border-amber-500 bg-amber-50 px-4 py-3 rounded-r text-[14px] md:text-[15px] leading-relaxed text-slate-700">
      <span className="font-semibold text-amber-800">Warning. </span>{children}
    </div>
  );
}
function Signup({ children }) {
  return (
    <div className="hb-callout hb-signup mt-4 mb-2 border-l-4 border-slate-400 bg-slate-100 px-4 py-3 rounded-r text-[14px] md:text-[15px] leading-relaxed text-slate-700">
      <span className="font-semibold text-slate-700 uppercase tracking-wide text-[12px]">At signup. </span>
      <span>{children}</span>
    </div>
  );
}

/* ───────────────────────── printable content ───────────────────────── */

function HandbookContent() {
  return (
    <article className="hb-article prose-slate max-w-none">
      {/* Cover / title block */}
      <header className="hb-cover mb-10 pb-6 border-b border-slate-200">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Production Pay Tracker</p>
        <h1 className="hb-h1 text-3xl md:text-5xl font-bold font-display text-slate-900 mt-2">
          VA Operator Handbook
        </h1>
        <p className="mt-3 text-slate-600 text-[15px] max-w-2xl">
          A step-by-step manual for virtual assistants and office admins who run day-to-day
          operations inside the Production Pay Tracker admin console.
        </p>
        <p className="mt-3 text-xs text-slate-500">
          Version {HANDBOOK_VERSION} · Last updated {HANDBOOK_LAST_UPDATED}
        </p>
      </header>

      {/* How to use */}
      <section className="hb-section">
        <P>
          Each section below is written as an operator manual: a short description of what the
          area does, followed by the exact steps a VA takes inside the admin console, followed by
          a short <em>&quot;At signup&quot;</em> callout that describes the default state a brand-new
          tenant sees. Follow the sections in order on day one. After that, use the table of
          contents to jump to a specific area.
        </P>
      </section>

      {/* 1. Getting Started */}
      <H2 id="getting-started">1. Getting Started &amp; Account Setup</H2>
      <P>
        The admin console is the single place a VA works from. Everything — employees, time,
        payroll, billing — lives here. Techs use a lighter employee app that writes into the same
        system; the VA never has to install anything separate.
      </P>
      <H3>Signing in</H3>
      <OL>
        <li>Open the tenant&apos;s admin URL and sign in with the credentials the owner provisioned for you.</li>
        <li>If sign-in fails, use <strong>Forgot password</strong> on the login screen; a reset link is emailed via Resend.</li>
        <li>
          Confirm the top of the sidebar shows the correct company name and logo. If it does not,
          you are in the wrong tenant.
        </li>
      </OL>
      <H3>Roles you will encounter</H3>
      <UL>
        <li><strong>Owner</strong> — the person who signed up and pays the bill. Can do everything, including billing and tenant settings.</li>
        <li><strong>Admin</strong> — the role a VA is usually given. Same operational powers as Owner except changing the subscription or deleting the tenant.</li>
        <li><strong>Lead Technician</strong> — a field tech who can also log reimbursements and approve crew finalizations.</li>
        <li><strong>Technician</strong> — field tech. Logs time, jobs, and daily submissions.</li>
      </UL>
      <Note>If you are shared across multiple tenants, each tenant has its own admin URL. Switch tenants by signing out and back in with the matching credentials.</Note>
      <Signup>
        The person who completed public signup is the sole <strong>Owner</strong>. No other employees
        exist. Seat count equals the plan default. The console is accessible immediately; the free
        trial is already active.
      </Signup>

      {/* 2. Employees */}
      <H2 id="employees">2. Employees</H2>
      <P>
        The <strong>Employees</strong> page is where you add, edit, and deactivate every person who
        works for the tenant. The system treats each employee as both a payroll record and a login,
        so every change here affects what techs see in the field.
      </P>
      <H3>Add a new employee</H3>
      <OL>
        <li>Go to <strong>Admin → Employees</strong>.</li>
        <li>Click <strong>Add employee</strong>.</li>
        <li>Enter first name, last name, email, and phone.</li>
        <li>Pick a <strong>role</strong>: Technician, Lead Technician, or Admin.</li>
        <li>
          Pick a <strong>pay type</strong>: Hourly, Commission, or Salary. The pay type decides
          what fields appear next (base rate for hourly, commission schedule for commission, annual
          salary for salary).
        </li>
        <li>
          Enter the <strong>state</strong> the employee works in. The payroll engine uses this to
          apply the right daily overtime rule (for example, California daily OT differs from the
          federal FLSA baseline).
        </li>
        <li>Set <strong>overtime eligible</strong>. Leave this on unless the owner has told you a specific exempt case applies.</li>
        <li>Click <strong>Save</strong>. The system emails the new employee an invite.</li>
      </OL>
      <H3>Edit, deactivate, reactivate</H3>
      <OL>
        <li>Click the row of the employee you want to change.</li>
        <li>Edit any field, then click <strong>Save</strong>. Changes take effect on new punches and new payroll calculations immediately.</li>
        <li>To stop paying someone without deleting their history, click <strong>Deactivate</strong>. They disappear from new submissions and payroll periods but their historical paystubs stay intact.</li>
        <li>To bring someone back, open the deactivated list and click <strong>Reactivate</strong>.</li>
      </OL>
      <H3>Resend invite, reset password, change email</H3>
      <OL>
        <li>Open the employee record.</li>
        <li>Use <strong>Resend invite</strong> if they never saw the first one.</li>
        <li>Use <strong>Send password reset</strong> if they&apos;re locked out.</li>
        <li>To change an email, edit the email field and save; the employee receives a confirmation.</li>
      </OL>
      <Warn>Active-employee count ties directly to billing seats. If you try to activate one more employee than the plan allows, the app returns a 402 and shows an upgrade link. Do not deactivate paying employees to shave the bill without telling the owner.</Warn>
      <Signup>
        No employees are seeded. The admin who signed up is the Owner. The seat cap equals the plan
        default (trial plan seats). The invite template is pre-written in Settings.
      </Signup>

      {/* 3. Daily Punches */}
      <H2 id="daily-punches">3. Daily Punches (Time Entries)</H2>
      <P>
        Daily punches are the raw clock-in and clock-out events techs record from the field. They
        feed everything downstream — daily submissions, overtime, payroll. The VA rarely creates
        punches from scratch, but regularly fixes forgotten or missed ones.
      </P>
      <H3>How a tech punches from the field</H3>
      <OL>
        <li>Tech opens the employee app on their phone, taps <strong>Time Clock</strong>, taps <strong>Clock in</strong>. Location is captured.</li>
        <li>At end of shift the tech taps <strong>Clock out</strong>. The system records the stop time.</li>
      </OL>
      <H3>Add, edit, or delete a punch on a tech&apos;s behalf</H3>
      <OL>
        <li>Go to <strong>Admin → Daily Punches</strong>.</li>
        <li>Pick the date and the tech.</li>
        <li>Click <strong>Add punch</strong>, enter the clock-in and clock-out times, and save. Both times must be the same date and clock-out must be after clock-in.</li>
        <li>To edit, click a row, change the times, save. The audit log records who edited what.</li>
        <li>To delete, click the row and choose <strong>Delete</strong>. Confirm. Deletion is logged.</li>
      </OL>
      <H3>Auto clock-out</H3>
      <P>
        If a tech forgets to clock out, the system auto closes the punch at a sensible cutoff so
        the day doesn&apos;t accrue phantom hours. Auto-closed punches are flagged so you can see
        which ones were machine-closed and verify them with the tech the next morning.
      </P>
      <H3>Rounding and the 8-day editable window</H3>
      <P>
        Punches are stored at the minute. Payroll rounds per the tenant&apos;s rounding policy
        (set in Settings). Punches can be edited for the last <strong>8 days</strong>. Older
        punches are locked by the back-fix window described in Section 8.
      </P>
      <Signup>
        Auto clock-out is enabled with the platform default thresholds. Rounding uses the platform
        default. There are no historical punches.
      </Signup>

      {/* 4. Jobs & Job Entries */}
      <H2 id="jobs">4. Jobs &amp; Job Entries</H2>
      <P>
        A <strong>job entry</strong> is one line of work a tech did on a job site — the service
        performed, the invoice amount, and the crew that did it. Commissions and daily
        submissions depend on job entries, so these have to be accurate.
      </P>
      <H3>Create a job entry on a tech&apos;s behalf</H3>
      <OL>
        <li>Open the tech&apos;s day under <strong>Admin → Submissions</strong> or <strong>Admin → Daily Punches</strong>.</li>
        <li>Click <strong>Add job entry</strong>.</li>
        <li>Pick the <strong>job type</strong> from the tenant&apos;s configured list.</li>
        <li>Enter the <strong>invoice amount</strong> and <strong>job address</strong>.</li>
        <li>Assign the <strong>lead</strong> and any <strong>assist</strong> crew. The commission split in Section 6 follows from this.</li>
        <li>Save. The entry attaches to that day&apos;s submission.</li>
      </OL>
      <H3>Editing historical job entries</H3>
      <P>
        Job entries are editable inside the 8-day window. Outside the window they are read-only
        and go through the Late-Submission Lane (Section 9).
      </P>
      <H3>Map pin behavior</H3>
      <P>
        Jobs render pins on the daily map. If you ever see a dialog hidden behind a map control,
        the fix is already shipped — the admin dialog sits at a higher z-index than the Leaflet
        overlay. If a dialog looks stuck behind the map, hard-reload once.
      </P>
      <Signup>
        No jobs or job entries exist. The job-type list is seeded with the platform baseline and
        can be edited in <strong>Settings → Job types</strong>.
      </Signup>

      {/* 5. Daily Submissions */}
      <H2 id="submissions">5. Daily Submissions &amp; Approvals</H2>
      <P>
        A <strong>daily submission</strong> is a tech&apos;s end-of-day roll-up: punches,
        job entries, reimbursements, and notes. The VA&apos;s job is to keep the approval queue
        clean so payroll closes cleanly every week.
      </P>
      <H3>Review a submission</H3>
      <OL>
        <li>Go to <strong>Admin → Submissions</strong>.</li>
        <li>Click a pending submission to open the detail view.</li>
        <li>
          Verify: hours match the day&apos;s punches, every job entry has an invoice amount, and
          the tech&apos;s notes don&apos;t flag something that needs the owner&apos;s eyes.
        </li>
      </OL>
      <H3>Approve, reject, or request changes</H3>
      <OL>
        <li>Click <strong>Approve</strong> if everything is in order. The submission is now immutable from the tech&apos;s side (edits go through you).</li>
        <li>Click <strong>Reject</strong> with a short note if something must be redone — the tech is notified and can refile.</li>
        <li>Use the comment field for small clarifications so the tech can re-submit without a full reject.</li>
      </OL>
      <H3>Bulk approve</H3>
      <P>
        For a quiet day with clean punches, use <strong>Bulk approve</strong> at the top of the
        list. Every bulk action is written to the audit log with your name on it.
      </P>
      <Signup>No pending submissions. The approval queue is empty. End-of-day reminders are on.</Signup>

      {/* 6. Commissions */}
      <H2 id="commissions">6. Commissions</H2>
      <P>
        Commission on a job entry follows the tenant&apos;s configured schedule, which maps job
        type and invoice amount to a lead and an assist payout. Overrides are allowed for the rare
        case where the schedule doesn&apos;t fit the deal.
      </P>
      <H3>Normal flow</H3>
      <OL>
        <li>Tech logs the job entry and names the lead and the assists.</li>
        <li>System calculates commission from the schedule and attaches it to that day&apos;s payroll.</li>
      </OL>
      <H3>Override a commission</H3>
      <OL>
        <li>Open the job entry.</li>
        <li>Click <strong>Override commission</strong>.</li>
        <li>Enter the new lead and assist amounts and a short reason.</li>
        <li>Save. The override shows on the paystub with the reason attached for audit.</li>
      </OL>
      <Warn>Overrides are the exception. Confirm with the owner before overriding on any amount larger than a token adjustment.</Warn>
      <Signup>
        The platform default commission schedule is active. The owner should review and update it
        in <strong>Settings → Commissions</strong> before the first payroll runs.
      </Signup>

      {/* 7. Payroll */}
      <H2 id="payroll">7. Payroll &amp; Paystubs</H2>
      <P>
        Payroll is the headline weekly workflow. The VA drives it from Monday morning through
        paystub distribution. The lifecycle is: Draft → Preview Close → Close → Finalize →
        Paystubs.
      </P>
      <H3>Weekly flow</H3>
      <OL>
        <li>Open <strong>Admin → Payroll</strong>. The current week shows as a Draft period.</li>
        <li>
          Confirm every tech&apos;s daily submissions for the week are approved (Section 5). The
          Draft period reflects whatever is approved right now.
        </li>
        <li>
          Click <strong>Preview close</strong>. The system runs the preview-close check and shows
          any unfinalized techs. See Section 8 for how to clear them.
        </li>
        <li>
          Click <strong>Close week</strong>. A confirmation dialog shows the final list of
          unfinalized techs. Use <strong>Notify these techs</strong> if any remain, or confirm the
          close if you&apos;re ready to lock the week.
        </li>
        <li>
          Click <strong>Finalize</strong>. Payroll is calculated: hours, FLSA federal weekly OT,
          state daily OT, commission, reimbursements, taxes, deductions.
        </li>
        <li>
          Click <strong>Generate paystubs</strong>. Each tech receives their paystub. Resend or
          void from the Paystubs page.
        </li>
      </OL>
      <H3>Reading the breakdown</H3>
      <P>
        Click any paystub to see the breakdown by day: regular hours, OT hours, commission, each
        reimbursement line, each deduction. If a line looks wrong, the audit log on that paystub
        shows every event that shaped it.
      </P>
      <Warn>Once you Finalize, the period is locked. Reopening is only possible through the Late-Submission Lane (Section 9) and leaves an audit trail.</Warn>
      <Signup>
        No payroll periods exist yet. The first weekly period is created automatically when the
        first punch of the week lands. State OT rules are inferred from the tenant&apos;s address;
        confirm these in <strong>Settings</strong> before the first finalize.
      </Signup>

      {/* 8. Phase 1 — Pre-Close Back-Fix */}
      <H2 id="phase1">8. Phase 1 — Pre-Close Back-Fix (8-Day Window)</H2>
      <P>
        Before a week is closed, admins and techs can edit anything inside a rolling 8-day window.
        This is what lets you fix a Monday punch on the following Monday without reopening payroll.
      </P>
      <H3>What is editable</H3>
      <UL>
        <li>Daily punches (add, edit, delete).</li>
        <li>Job entries attached to any day in the window.</li>
        <li>Daily submission approvals (approve, reject, request changes).</li>
        <li>Reimbursements for days in the window.</li>
      </UL>
      <H3>Preview-close before you close</H3>
      <OL>
        <li>Go to <strong>Admin → Payroll</strong>.</li>
        <li>Click <strong>Preview close</strong>. The system lists any tech whose submissions for the week are not yet finalized.</li>
        <li>
          Click <strong>Notify these techs</strong> to email the list a reminder to finalize their
          week. The email template is pre-written.
        </li>
        <li>Wait for techs to finalize, re-run Preview close, then close the week.</li>
      </OL>
      <Note>Preview-close is a dry run. It does not lock anything. Run it as often as you like until the list is empty.</Note>
      <Signup>
        The 8-day window is enabled by default. The notification template is pre-written. The
        preview-close banner is visible on the Payroll page.
      </Signup>

      {/* 9. Phase 2 — Late-Submission Lane */}
      <H2 id="phase2">9. Phase 2 — Late-Submission Lane (Post-Close Back-Finalize)</H2>
      <P>
        After a week is closed, techs can still file a <strong>late-finalize request</strong> if
        they missed the window. The admin reviews each request on a dedicated tab and either
        approves — which reopens the week, folds in the changes, regenerates payroll, and re-locks
        the period — or rejects with a note.
      </P>
      <H3>How a tech files a late-finalize request</H3>
      <OL>
        <li>Tech opens the day they missed in the employee app.</li>
        <li>Tech taps <strong>Request late finalize</strong>.</li>
        <li>Tech picks a reason from the pre-seeded list (missed clock-out, forgot job entry, etc.) and adds detail.</li>
        <li>Request lands in the admin <strong>Late Submissions</strong> tab for review.</li>
      </OL>
      <H3>Approve or reject a late request</H3>
      <OL>
        <li>Go to <strong>Admin → Payroll → Late Submissions</strong>.</li>
        <li>Open a pending request. Read the reason and the days affected.</li>
        <li>
          Click <strong>Approve and apply</strong>. The orchestrator:
          <UL>
            <li>Reopens the payroll period.</li>
            <li>Folds in the tech&apos;s late finalize data for the specified days.</li>
            <li>Regenerates payroll for that period.</li>
            <li>Re-locks the period.</li>
            <li>Writes every step to the audit log.</li>
          </UL>
        </li>
        <li>Or click <strong>Reject</strong> with a short note if the request is invalid. The tech is notified.</li>
      </OL>
      <Warn>Approving a late request can change a paystub that has already been sent. If taxes were already filed for the period, loop in the owner before approving.</Warn>
      <Signup>
        The Late-Submission Lane is enabled by default. The queue is empty. The reason list is
        pre-seeded.
      </Signup>

      {/* 10. Reimbursements */}
      <H2 id="reimbursements">10. Reimbursements</H2>
      <P>
        A reimbursement is an out-of-pocket expense a tech paid for and expects back on their
        paystub. Leads file them; admins approve.
      </P>
      <H3>Daily workflow</H3>
      <OL>
        <li>Lead tech files the reimbursement from the employee app with a category, amount, and note.</li>
        <li>Go to <strong>Admin → Reimbursements</strong>.</li>
        <li>Review and either <strong>Approve</strong> or <strong>Deny</strong> each request.</li>
        <li>Approved reimbursements flow into the current week&apos;s paystub for that tech.</li>
      </OL>
      <Signup>
        The reimbursement categories are seeded with a default list. The queue is empty. Photo
        uploads on reimbursements are on the roadmap (Phase 2) and not available yet.
      </Signup>

      {/* 11. Time Off */}
      <H2 id="time-off">11. Time Off</H2>
      <P>
        Time off is logged by the VA on behalf of techs during Phase 1 of the platform. Self-serve
        requests are on the roadmap.
      </P>
      <H3>Log time off on a tech&apos;s behalf</H3>
      <OL>
        <li>Open the tech&apos;s week.</li>
        <li>Click <strong>Add time off</strong>.</li>
        <li>Pick the date(s), the type (PTO, unpaid, sick), and hours.</li>
        <li>Save. Time-off hours feed into the paystub.</li>
      </OL>
      <Signup>
        There are no PTO balances. Accrual is off until the owner configures it. The self-serve
        request flow is not yet enabled.
      </Signup>

      {/* 12. Reporting */}
      <H2 id="reports">12. Reporting &amp; Exports</H2>
      <P>
        The <strong>Reports</strong> page is where the VA pulls weekly summaries, paystub exports,
        payroll CSVs, and commission reports to hand to the owner or an outside bookkeeper.
      </P>
      <H3>Common pulls</H3>
      <UL>
        <li><strong>Weekly summary</strong> — total hours and payout per tech for a given week.</li>
        <li><strong>Paystub export</strong> — PDFs of every paystub in a range, zipped for download.</li>
        <li><strong>Payroll CSV</strong> — raw payroll rows ready to import to a bookkeeping system.</li>
        <li><strong>Commission report</strong> — lead and assist payouts by tech, by week.</li>
      </UL>
      <Signup>Exports are available but return empty files until there is data.</Signup>

      {/* 13. Billing */}
      <H2 id="billing">13. Billing &amp; Subscription</H2>
      <P>
        Billing is one of the few areas where the VA mostly observes and only acts on explicit
        instruction from the owner. Pricing and plan changes affect the owner&apos;s card and
        every tech&apos;s access.
      </P>
      <H3>What the Billing page shows</H3>
      <UL>
        <li>Current plan and seat count.</li>
        <li>Trial status and days remaining, if applicable.</li>
        <li>Upcoming invoice date and amount.</li>
        <li>Past invoices.</li>
      </UL>
      <H3>Handling the 402 &quot;seat cap reached&quot; message</H3>
      <OL>
        <li>If you try to activate an employee past the cap, the app returns HTTP 402 and shows an upgrade link.</li>
        <li>Do <em>not</em> deactivate a paying employee silently to make room.</li>
        <li>Tell the owner. The owner clicks the upgrade link, picks a bigger plan, and the activation succeeds on retry.</li>
      </OL>
      <Signup>
        The tenant is on the trial plan with the default seat count and trial window. No invoices
        yet. The upgrade link is live from day one.
      </Signup>

      {/* 14. Growth Center */}
      <H2 id="growth">14. AI Growth Center</H2>
      <P>
        The Growth Center is a set of lanes that produce leads, content drafts, partnership ideas,
        and alerts for the tenant. Most lanes run on the Emergent Universal Key with no owner setup.
        A few lanes need third-party API keys and will say so clearly if they are not configured.
      </P>
      <H3>Running a lane</H3>
      <OL>
        <li>Go to <strong>Growth Center → Opportunities</strong> (or the relevant lane tab).</li>
        <li>Pick the lane and click <strong>Run</strong>.</li>
        <li>Review the results. Promote qualified items to the Leads or Pipeline board as instructed.</li>
      </OL>
      <H3>Missing key states</H3>
      <P>
        If you see <em>&quot;API key not configured&quot;</em> or a lane returns zero results with
        a configuration hint, flag it to the owner. JSearch (RapidAPI) and Serper.dev are the two
        most common keys techs forget to add.
      </P>
      <Signup>
        Growth Center is accessible from day one. Emergent-key lanes work out of the box. JSearch
        and Serper lanes return a clear &quot;not configured&quot; state until the owner adds those
        keys in <strong>Settings</strong>.
      </Signup>

      {/* 15. Marketing surfaces */}
      <H2 id="marketing">15. Public Marketing Surfaces</H2>
      <P>
        For context: the platform ships two public pages that drive leads into the signup flow.
        The VA does not edit these, but should know they exist in case the owner asks where a
        lead came from.
      </P>
      <UL>
        <li>
          <strong>/demo</strong> — the main homepage. Pricing cards live at the top. UTM parameters
          are preserved into signup. Meta Pixel fires <code>PageView</code>, <code>Lead</code>, and
          <code> StartTrial</code> dynamically.
        </li>
        <li>
          <strong>/hvac</strong> — a dedicated HVAC landing page. Same pricing and UTM handling,
          HVAC-specific copy and imagery.
        </li>
      </UL>
      <Signup>Not applicable — these pages are owned by the platform, not by the tenant.</Signup>

      {/* 16. VA Playbooks */}
      <H2 id="playbooks">16. Common VA Playbooks</H2>

      <H3>A tech forgot to clock out</H3>
      <OL>
        <li>Open <strong>Admin → Daily Punches</strong>, pick the date and the tech.</li>
        <li>If auto clock-out already closed the day, confirm the stop time with the tech.</li>
        <li>Edit the punch to the correct stop time. Save.</li>
        <li>If the day&apos;s submission was already approved, re-approve after the edit.</li>
      </OL>

      <H3>A tech needs to add a job after the week closed</H3>
      <OL>
        <li>Tell the tech to open the affected day and tap <strong>Request late finalize</strong>.</li>
        <li>Review the request under <strong>Admin → Payroll → Late Submissions</strong>.</li>
        <li>Approve and apply. The orchestrator reopens the week, folds in the job, regenerates payroll, re-locks.</li>
        <li>Confirm the tech&apos;s new paystub reflects the added job.</li>
      </OL>

      <H3>Payroll is off by a day for one tech</H3>
      <OL>
        <li>Open the tech&apos;s paystub for the affected week.</li>
        <li>Check the day-by-day breakdown for a dropped day.</li>
        <li>Check <strong>Admin → Daily Punches</strong> for the same date. If a punch exists but isn&apos;t on the paystub, verify the submission was approved before close.</li>
        <li>If the week is still open, re-approve the submission; the next regenerate picks it up.</li>
        <li>If the week is closed, run the Late-Submission Lane for that day.</li>
      </OL>

      <H3>Owner wants to add a new hire today</H3>
      <OL>
        <li>Confirm the owner has a free seat (<strong>Admin → Billing</strong>). If not, ask them to upgrade before you proceed.</li>
        <li>Go to <strong>Admin → Employees → Add employee</strong>. Fill in all fields from Section 2.</li>
        <li>Save. The invite email goes out automatically.</li>
        <li>Confirm with the new hire that they received the invite and can sign in on their phone.</li>
      </OL>

      <H3>Trial is ending, owner wants to upgrade</H3>
      <OL>
        <li>Direct the owner to <strong>Admin → Billing</strong>.</li>
        <li>Owner picks a plan and completes checkout. Stripe handles the card.</li>
        <li>Confirm the plan line at the top of the Billing page reflects the new tier.</li>
      </OL>

      {/* 17. Escalation */}
      <H2 id="escalation">17. Escalation &amp; Support</H2>
      <H3>Resolve as a VA</H3>
      <UL>
        <li>Routine employee adds, edits, deactivations.</li>
        <li>Daily punch corrections inside the 8-day window.</li>
        <li>Submission approvals, rejections, and comment-based clarifications.</li>
        <li>Reimbursement approvals under the per-item cap the owner sets.</li>
        <li>Weekly payroll close and paystub distribution.</li>
      </UL>
      <H3>Escalate to the owner</H3>
      <UL>
        <li>Commission overrides larger than a token adjustment.</li>
        <li>Any 402 seat-cap event.</li>
        <li>Late-finalize requests that would alter a paystub for a period whose taxes were filed.</li>
        <li>Billing or Stripe-related changes.</li>
        <li>Growth Center lanes reporting missing API keys.</li>
      </UL>
      <H3>Platform support</H3>
      <P>
        If the owner needs direct platform support (billing issues, outages, bugs the console
        doesn&apos;t let you resolve), direct them to the support channel listed in their signup
        welcome email. Do not share credentials or billing details over any other channel.
      </P>

      <footer className="hb-footer mt-16 pt-6 border-t border-slate-200 text-xs text-slate-500">
        End of handbook · Production Pay Tracker · VA Operator Handbook · Version {HANDBOOK_VERSION}
      </footer>
    </article>
  );
}

/* ───────────────────────── page shell with TOC + print styles ───────────────────────── */

export default function AdminHandbook() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  // Highlight the active TOC item as the user scrolls.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const printStyles = useMemo(
    () => `
      @media print {
        @page { size: Letter; margin: 0.6in 0.6in 0.8in 0.6in; }
        html, body { background: #fff !important; }
        /* Hide layout chrome: sidebar, mobile top bar, bottom nav, subscription banner, and any element flagged .hb-no-print */
        aside, header, .hb-no-print,
        nav.md\\:hidden, nav[class*="bottom-0"],
        [class*="subscription-banner"] { display: none !important; }
        /* Reset the main content container padding/offset so the article fills the page */
        main { padding: 0 !important; margin: 0 !important; }
        main > div { padding: 0 !important; margin: 0 !important; max-width: none !important; }
        .hb-shell { padding: 0 !important; margin: 0 !important; max-width: none !important; display: block !important; grid-template-columns: none !important; }
        .hb-toc { display: none !important; }
        .hb-article {
          color: #111 !important;
          font-family: Georgia, 'Times New Roman', serif;
          font-size: 11pt;
          line-height: 1.55;
          max-width: none !important;
        }
        .hb-article .hb-h1 { font-size: 22pt; }
        .hb-article .hb-h2 {
          font-size: 15pt;
          page-break-before: always;
          break-before: page;
          page-break-after: avoid;
          break-after: avoid;
          margin-top: 0 !important;
        }
        .hb-article .hb-cover { page-break-after: always; break-after: page; }
        .hb-article .hb-h3 { font-size: 12pt; page-break-after: avoid; break-after: avoid; }
        .hb-article .hb-p, .hb-article .hb-ol, .hb-article .hb-ul { orphans: 3; widows: 3; }
        .hb-article .hb-callout {
          background: #f6f6f6 !important;
          border-left: 3px solid #666 !important;
          color: #111 !important;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .hb-article .hb-footer { page-break-before: avoid; break-before: avoid; }
      }
    `,
    []
  );

  const handlePrint = () => {
    // Mark the root so print CSS can isolate it (we also keep the global style tag).
    window.print();
  };

  return (
    <div className="hb-print-root">
      <style dangerouslySetInnerHTML={{ __html: printStyles }} />

      {/* Screen-only toolbar */}
      <div className="hb-no-print flex items-center justify-between mb-6" data-testid="handbook-toolbar">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <BookOpen className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold font-display text-slate-900">VA Operator Handbook</h1>
            <p className="text-xs text-slate-500">
              Version {HANDBOOK_VERSION} · Last updated {HANDBOOK_LAST_UPDATED}
            </p>
          </div>
        </div>
        <Button onClick={handlePrint} data-testid="handbook-print-btn" className="bg-slate-900 hover:bg-slate-800">
          <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
        </Button>
      </div>

      <div className="hb-shell grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* Sticky TOC */}
        <aside className="hb-toc hb-no-print hidden lg:block">
          <nav className="sticky top-24 border-l border-slate-200 pl-4" data-testid="handbook-toc">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
              Contents
            </p>
            <ul className="space-y-1.5">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    data-testid={`toc-${s.id}`}
                    className={`block text-[13px] leading-snug py-1 transition-colors ${
                      activeId === s.id
                        ? "text-slate-900 font-semibold"
                        : "text-slate-500 hover:text-slate-900"
                    }`}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Body */}
        <div className="min-w-0">
          <HandbookContent />
        </div>
      </div>
    </div>
  );
}
